/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import debounce from "lodash.debounce";
import { When } from "react-if";
import {
  Sidebar,
  Table,
  Input,
  Spacer,
  ExtendedPaginationBox,
  FlexContainer,
  theme,
} from "@nordcloud/gnui";
import { LoaderWrap, ListLoader } from "~/components";
import { useDisclosure, useApplicationsPagination } from "~/hooks";
import { mapInputStatus, noop } from "~/tools";

const DEBOUNCE_TIMEOUT_MS = 350;

type ApplicationSelectorOption = {
  nid: string;
  id: string;
  name: string;
  environments: {
    name: string;
    id: string;
    nid: string;
  }[];
};

type Props = {
  value: string;
  onChange: (application: ApplicationSelectorOption) => void;
  placeholder?: string;
  status?: "error" | "success";
};

export function ApplicationInput({
  value,
  onChange,
  placeholder,
  status,
}: Props) {
  const { isOpen, close, open } = useDisclosure();
  const {
    onSearchChange,
    selectedApp,
    applications,
    count,
    page,
    pageSize,
    loading,
    setPage,
  } = useApplicationSearch(value);

  const apply = (application: ApplicationSelectorOption) => {
    onChange(application);
    close();
  };

  const displayValue = selectedApp ? getDisplayName(selectedApp) : value;

  function SidebarFooter() {
    return (
      <FlexContainer
        direction="column"
        css={{ width: "100%", paddingBottom: theme.spacing.spacing07 }}
      >
        <div css={{ width: "100%" }}>
          <ExtendedPaginationBox
            small
            count={count}
            from={page * pageSize}
            setSize={noop}
            size={pageSize}
            setPage={(newPage) => {
              setPage(newPage / pageSize);
            }}
          />
        </div>
      </FlexContainer>
    );
  }

  return (
    <>
      <Input
        type="text"
        value={displayValue}
        name="app-selector"
        placeholder={placeholder || "Select Application"}
        status={mapInputStatus(status)}
        onClick={open}
      />
      <Sidebar
        isOpen={isOpen}
        title="Select Application"
        footer={
          <When condition={!loading}>
            <SidebarFooter />
          </When>
        }
        onClick={close}
      >
        <Input
          type="text"
          name="filter"
          placeholder="Search"
          onChange={onSearchChange}
        />
        <Spacer height={theme.spacing.spacing04} />
        <LoaderWrap loading={loading} Component={ListLoader}>
          <Table small hoverline>
            <Table.thead>
              <Table.tr>
                <Table.th>Application name</Table.th>
                <Table.th>Environments</Table.th>
              </Table.tr>
            </Table.thead>
            <Table.tbody>
              {applications.map((application) => (
                <Table.tr
                  key={application.nid}
                  onClick={() => apply(application)}
                >
                  <Table.td>{application.name}</Table.td>
                  <Table.td>{application.environments.length}</Table.td>
                </Table.tr>
              ))}
            </Table.tbody>
          </Table>
        </LoaderWrap>
      </Sidebar>
    </>
  );
}

function useApplicationSearch(value: string) {
  const { data, setPage, setApplicationName, ...rest } =
    useApplicationsPagination();

  const applications = data.applicationsPaginated?.results ?? [];
  const selectedApp = applications.find(({ id }) => id === value);

  const debouncedSearch = debounce((searchValue: string) => {
    setPage(0);
    setApplicationName(searchValue);
  }, DEBOUNCE_TIMEOUT_MS);

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    debouncedSearch(searchValue);
  };

  return {
    onSearchChange,
    selectedApp,
    applications,
    count: data?.applicationsPaginated?.count ?? 0,
    setPage,
    ...rest,
  };
}

function getDisplayName<T extends { name: string; displayName?: string }>(
  input: T
) {
  return input.displayName || input.name;
}
